import React, { useContext, Fragment, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Modal,
  Button,
  Accordion,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';
import { useAccordionButton } from 'react-bootstrap/AccordionButton';
import { ContextUser } from '../../../providers/ContextUser';
import Item from './item';
import {
  CustonDropdownItem,
  CustonDropdownIcon,
  CustonDropdownText,
  AccordionItem,
  AccordionButton,
  AccordionBody,
  CustomButton,
  StyledNav,
  StyledUl,
  StyledModalHeader,
  StyledModalFooter,
  StyledModalBody,
  StyledModal,
  Separator,
} from './styles';

import { AiOutlineDashboard } from 'react-icons/ai';
import { IoBusinessOutline } from 'react-icons/io5';
import { HiOutlineDocumentDuplicate } from 'react-icons/hi2';
import { RiCustomerService2Line } from 'react-icons/ri';
import { IoLibraryOutline } from 'react-icons/io5';
import { LuAlertTriangle } from 'react-icons/lu';
import { MdOutlineSlowMotionVideo, MdOutlineSecurity } from 'react-icons/md';
import { FiUsers } from 'react-icons/fi';

const Navigation = ({ isCollapsed, toggleSidebar }) => {
  const { handleLogout, currentOrganization, currentUser } = useContext(ContextUser);
  const navigate = useNavigate();
  const location = useLocation();
  const [showModal, setShowModal] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoTitle, setVideoTitle] = useState('Vídeo');
  const [hoveredItem, setHoveredItem] = useState(null);
  const [activeEventKey, setActiveEventKey] = useState(null);

  // Função para determinar o caminho correto dos incidentes baseado no nível do usuário
  const getIncidentsPath = () => {
    if (!currentOrganization?.id) return '/';
    
    // Determina o caminho baseado no nível hierárquico do usuário
    const currentPath = location.pathname;
    
    if (currentPath.includes('/prefeitura/secretaria/unidade/departamento/')) {
      const departmentId = currentPath.split('/')[5];
      return `/prefeitura/secretaria/unidade/departamento/${departmentId}/incidents`;
    } else if (currentPath.includes('/prefeitura/secretaria/unidade/')) {
      const unitId = currentPath.split('/')[4];
      return `/prefeitura/secretaria/unidade/${unitId}/incidents`;
    } else if (currentPath.includes('/prefeitura/secretaria/')) {
      const branchId = currentPath.split('/')[3];
      return `/prefeitura/secretaria/${branchId}/incidents`;
    } else if (currentPath.includes('/prefeitura/')) {
      return `/prefeitura/${currentOrganization.id}/incidents`;
    }
    
    // Fallback para nível de organização
    return `/prefeitura/${currentOrganization.id}/incidents`;
  };

  const buildNavigationMenu = () => {
    const menuItens = [
      {
        title: 'Dashboard',
        path: '/',
        icon: <AiOutlineDashboard />,
        isActive: true,
        position: 1,
      },
      {
        title: 'Treinamentos',
        path: '/',
        icon: <MdOutlineSlowMotionVideo />,
        isActive: true,
        isTraining: true,
        position: 8,
      }
    ];

    if (currentOrganization) {
      const canAccessPrivileged = ['ADMIN', 'ACCESS_ORG_LEVEL'].includes(currentUser?.role);
      const canAccessOrganizations = currentUser?.role === 'ADMIN';
      const organizationMenuItens = [
        {
          title: 'Usuários',
          path: currentOrganization ? `/prefeitura/${currentOrganization.id}/usuarios` : '/usuarios',
          icon: <FiUsers id='icon-users-nav' />,
          isActive: canAccessPrivileged,
          isDisabled: !canAccessPrivileged,
          position: 2,
        },
        {
          title: 'Organizações',
          path: '/organizacoes',
          icon: <IoBusinessOutline id='icon-organizations-nav' />,
          isActive: canAccessOrganizations,
          isDisabled: !canAccessOrganizations,
          position: 3,
        },
        {
          title: 'Service Desk',
          path: '/service-desk',
          icon: <RiCustomerService2Line id='icon-service-desk-nav' />,
          isActive: true,
          position: 4,
        },
        {
          title: 'Gestão de Incidentes',
          path: getIncidentsPath(),
          icon: <LuAlertTriangle id='icon-incidents-nav' />,
          isActive: canAccessPrivileged,
          isDisabled: !canAccessPrivileged,
          position: 5,
        },
        {
          title: 'Cybersecurity',
          path: '/cybersecurity',
          icon: <MdOutlineSecurity />,
          isActive: false,
          isDisabled: true,
          position: 6,
        },
        {
          title: 'Modelos',
          path: '/models',
          icon: <HiOutlineDocumentDuplicate />,
          isActive: true,
          position: 7,
        },
        {
          title: 'Biblioteca',
          path: '/library',
          icon: <IoLibraryOutline />,
          isActive: true,
          position: 8,
        },
      ];

      menuItens.push(...organizationMenuItens);
    }

    menuItens.sort((a, b) => a.position - b.position);

    return menuItens;
  };

  const handleShow = (url) => {
    const videoId = url.split('v=')[1];
    const embedUrl = `https://www.youtube.com/embed/${videoId}`;
    setVideoUrl(embedUrl);
    setShowModal(true);
  };

  const handleClose = () => setShowModal(false);

  const action = (path, isDisabled) => {
    if (isDisabled) {
      return; // Não executa ação se estiver desabilitado
    }
    
    if (path === 'logout') {
      handleLogout();
    } else if (path.startsWith('https://www.youtube.com')) {
      handleShow(path);
    } else {
      navigate(path);
    }
  };

  const CustomToggle = ({
    children,
    eventKey,
    isHovered,
    setHoveredItem,
    setActiveEventKey,
    activeEventKey,
    index,
    isCollapsed,
    toggleSidebar,
  }) => {
    const decoratedOnClick = useAccordionButton(eventKey);

    const handleClick = () => {
      decoratedOnClick();
      setActiveEventKey(activeEventKey === eventKey ? null : eventKey);
      if (isCollapsed && activeEventKey !== eventKey) {
        toggleSidebar();
      }
    };

    return (
      <CustomButton
        type='button'
        onClick={handleClick}
        onMouseEnter={() => setHoveredItem(index)}
        onMouseLeave={() => setHoveredItem(null)}
        isHovered={isHovered}
        isCollapsed={isCollapsed}
      >
        {children}
      </CustomButton>
    );
  };

  const handleToggle = (index) => {
    setActiveEventKey(index === hoveredItem ? null : index);
  };

  useEffect(() => {
    if (isCollapsed && activeEventKey !== null) {
      setActiveEventKey(null);
    }
  }, [isCollapsed, activeEventKey]);

  // Garante que qualquer tooltip/hover seja fechado ao mudar de rota
  useEffect(() => {
    setHoveredItem(null);
  }, [location.pathname]);

  const renderTooltip = (text) => (props) =>
    (
      <Tooltip id='button-tooltip' {...props}>
        {text}
      </Tooltip>
    );

  const menuItems = buildNavigationMenu();
  const mainMenuItems = menuItems.filter(item => !item.isLogout && !item.isTraining);
  const trainingItems = menuItems.filter(item => item.isTraining);
  const logoutItems = menuItems.filter(item => item.isLogout);

  return (
    <>
      <StyledNav>
        <StyledUl isCollapsed={isCollapsed}>
          {/* Menu Principal */}
          {mainMenuItems.map((item, index) => (
            <Item
              key={index}
              title={item.title}
              path={item.path}
              icon={item.icon}
              isActive={item.isActive}
              isLogout={item.isLogout}
              action={(path) => action(path, item.isDisabled)}
              isHovered={hoveredItem === index}
              onMouseEnter={() => setHoveredItem(index)}
              onMouseLeave={() => setHoveredItem(null)}
              onClick={() => handleToggle(index)}
              isCollapsed={isCollapsed}
            />
          ))}

          {/* Separador */}
          {!isCollapsed && mainMenuItems.length > 0 && (trainingItems.length > 0 || logoutItems.length > 0) && (
            <Separator />
          )}

          {/* Treinamentos */}
          {trainingItems.map((item, index) => {
            const trainingIndex = mainMenuItems.length + index;
            const eventKey = index.toString();
            return (
              <Fragment key={`training-${index}`}>
                <OverlayTrigger
                  placement='right'
                  overlay={renderTooltip(item.title)}
                  trigger={[]}
                  show={hoveredItem === trainingIndex}
                >
                  <li
                    id={`nav-training-item-${index}`}
                    onMouseEnter={() => setHoveredItem(trainingIndex)}
                    onMouseLeave={() => setHoveredItem(null)}
                  >
                    <Accordion activeKey={activeEventKey}>
                      <AccordionItem eventKey={eventKey}>
                        <AccordionButton>
                          <CustomToggle
                            eventKey={eventKey}
                            isHovered={hoveredItem === trainingIndex}
                            setHoveredItem={setHoveredItem}
                            setActiveEventKey={setActiveEventKey}
                            activeEventKey={activeEventKey}
                            index={trainingIndex}
                            isCollapsed={isCollapsed}
                            toggleSidebar={toggleSidebar}
                          >
                            {item.icon}
                            {!isCollapsed && <span>{item.title}</span>}
                          </CustomToggle>
                        </AccordionButton>
                        <Accordion.Collapse eventKey={eventKey}>
                          <AccordionBody>
                            <CustonDropdownItem
                              href='#'
                              onClick={() => {
                                setVideoTitle('LGPD nas entidades públicas');
                                handleShow(
                                  'https://www.youtube.com/watch?v=WmSYGCcQ6J0'
                                );
                              }}
                            >
                              <CustonDropdownIcon />
                              <CustonDropdownText>
                                LGPD nas entidades públicas
                              </CustonDropdownText>
                            </CustonDropdownItem>
                            <CustonDropdownItem
                              href='#'
                              onClick={() => {
                                setVideoTitle('Matriz de Risco');
                                handleShow(
                                  'https://www.youtube.com/watch?v=B0QHBXsDQNg'
                                );
                              }}
                            >
                              <CustonDropdownIcon />
                              <CustonDropdownText>
                                Matriz de Risco
                              </CustonDropdownText>
                            </CustonDropdownItem>
                          </AccordionBody>
                        </Accordion.Collapse>
                      </AccordionItem>
                    </Accordion>
                  </li>
                </OverlayTrigger>
              </Fragment>
            );
          })}

          {/* Separador antes do logout */}
          {!isCollapsed && logoutItems.length > 0 && (
            <Separator />
          )}

          {/* Logout */}
          {logoutItems.map((item, index) => {
            const logoutIndex = mainMenuItems.length + trainingItems.length + index;
            return (
              <Item
                key={`logout-${index}`}
                title={item.title}
                path={item.path}
                icon={item.icon}
                isActive={item.isActive}
                isLogout={item.isLogout}
                action={(path) => action(path, item.isDisabled)}
                isHovered={hoveredItem === logoutIndex}
                onMouseEnter={() => setHoveredItem(logoutIndex)}
                onMouseLeave={() => setHoveredItem(null)}
                onClick={() => handleToggle(logoutIndex)}
                isCollapsed={isCollapsed}
              />
            );
          })}
        </StyledUl>
      </StyledNav>

      <StyledModal id='modal-video' show={showModal} onHide={handleClose} size='lg'>
        <StyledModalHeader id='modal-video-header' closeButton>
          <Modal.Title>{videoTitle}</Modal.Title>
        </StyledModalHeader>
        <StyledModalBody id='modal-video-body'>
          <iframe
            id='modal-video-iframe'
            width='100%'
            height='400px'
            src={videoUrl}
            allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
            allowFullScreen
          ></iframe>
        </StyledModalBody>
        <StyledModalFooter>
          <Button variant='secondary' onClick={handleClose}>
            Fechar
          </Button>
        </StyledModalFooter>
      </StyledModal>
    </>
  );
};

export default Navigation;
